import { sharesEdge } from "../document/roomPolygons";
import { isPrimaryButton } from "../gesture/pointer";
import { roomPolygonsOf } from "./autoZoneCreate";
import type { Tool } from "./Tool";

/**
 * Pick rooms detected from the wall graph (drawn by `AutoZoneSvg`). A
 * click adds the room as a zone of its own, or removes it again;
 * Shift-click merges it into the previous pick when the two share a
 * wall. Nothing is created until the picker card approves the set.
 */
export const createAutoZoneTool = (): Tool => ({
  kind: "auto-zone",
  cursor: "crosshair",
  onPointerDown(context, event) {
    if (!isPrimaryButton(event)) return;
    const element = event.target instanceof Element ? event.target : null;
    const indexAttribute =
      element?.closest<SVGElement>("[data-room-index]")?.dataset.roomIndex;
    if (indexAttribute === undefined) return;
    const index = Number(indexAttribute);
    const state = context.store.getState();
    const picks = state.chrome.autoZonePicks;
    const owner = picks.findIndex((pick) => pick.rooms.includes(index));
    if (owner >= 0) {
      state.setChrome({
        autoZonePicks: picks
          .map((pick, position) =>
            position === owner
              ? { ...pick, rooms: pick.rooms.filter((room) => room !== index) }
              : pick
          )
          .filter((pick) => pick.rooms.length > 0)
      });
      return;
    }
    const last = picks[picks.length - 1];
    const rooms = roomPolygonsOf(state);
    const canMerge =
      event.shiftKey &&
      !!last &&
      !!rooms[index] &&
      last.rooms.some(
        (room) => !!rooms[room] && sharesEdge(rooms[room], rooms[index])
      );
    if (canMerge) {
      state.setChrome({
        autoZonePicks: [
          ...picks.slice(0, -1),
          { ...last, rooms: [...last.rooms, index] }
        ]
      });
      return;
    }
    // A new pick starts with the panel's zone, else the one used last, so
    // a run of rooms in the same category needs no re-choosing.
    state.setChrome({
      autoZonePicks: [
        ...picks,
        {
          rooms: [index],
          name: null,
          tag: state.chrome.zoneToolTag ?? last?.tag ?? null
        }
      ]
    });
  },
  onPointerMove: () => {},
  onPointerUp: () => {},
  cancel(context) {
    if (context.store.getState().chrome.autoZonePicks.length > 0) {
      context.store.getState().setChrome({ autoZonePicks: [] });
    }
  }
});
